import _extends from "@babel/runtime/helpers/extends";
import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef } from 'react';
import { createEventChannel } from './core';
const emptyObject = {};
export const createBreadcrumbAPI = () => {
  const context = /*#__PURE__*/createContext(() => undefined);
  const useGetBreadcrumbs = () => useContext(context);
  const breadcrumbMountedEventChannel = createEventChannel();
  const {
    useEventDispatch
  } = breadcrumbMountedEventChannel;
  const EmitEventOnMount = ({
    name,
    getMountedBreadcrumbPayload
  }) => {
    const eventDispatch = useEventDispatch();
    useEffect(() => {
      // Should always be defined at this point.
      eventDispatch(getMountedBreadcrumbPayload());
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [name]);
    return null;
  };
  const Breadcrumb = ({
    children,
    ...breadcrumbPayload
  }) => {
    const getParentBreadcrumbs = useGetBreadcrumbs();

    // TODO: Type inference doesn't seem to understand that children and name are never a part of breadcrumbPayload. Investigate and fix.
    const castBreadcrumbPayload = breadcrumbPayload;

    // Prevents breadcrumb callback from being recreated everytime props change/every-render
    const breadcrumbRef = useRef(castBreadcrumbPayload);
    useLayoutEffect(() => {
      // Ensures that the breadcrumb only updates when it component is mounted
      breadcrumbRef.current = castBreadcrumbPayload;
    });

    // TODO: Memoize this in the future?
    const getMountedBreadcrumbPayload = useCallback(() => {
      var _getParentBreadcrumbs, _getParentBreadcrumbs2;
      return {
        breadcrumbs: [...((_getParentBreadcrumbs = (_getParentBreadcrumbs2 = getParentBreadcrumbs()) === null || _getParentBreadcrumbs2 === void 0 ? void 0 : _getParentBreadcrumbs2.breadcrumbs) !== null && _getParentBreadcrumbs !== void 0 ? _getParentBreadcrumbs : []), breadcrumbRef.current],
        payload: breadcrumbRef.current
      };
    }, [getParentBreadcrumbs]);
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(EmitEventOnMount, {
      name: breadcrumbPayload.name,
      getMountedBreadcrumbPayload: getMountedBreadcrumbPayload
    }), /*#__PURE__*/React.createElement(context.Provider, {
      value: getMountedBreadcrumbPayload
    }, children));
  };
  return {
    /**
     * An event channel whos event represents that a breadcrumb was mounted
     */
    breadcrumbMountedEventChannel,
    Breadcrumb,
    useGetBreadcrumbs
  };
};
export const createBreadcrumbedEventChannel = ({
  useGetBreadcrumbs
}, createEventChannelFn = createEventChannel) => {
  const {
    Listener,
    useEventDispatch
  } = createEventChannelFn();
  const useBreadcrumbedEventDispatch = () => {
    const eventDispatch = useEventDispatch();
    const getParentBreadcrumbs = useGetBreadcrumbs();
    const breadcrumbedEventDispatch = useCallback(payload => {
      var _getParentBreadcrumbs3, _getParentBreadcrumbs4;
      return eventDispatch({
        breadcrumbs: (_getParentBreadcrumbs3 = (_getParentBreadcrumbs4 = getParentBreadcrumbs()) === null || _getParentBreadcrumbs4 === void 0 ? void 0 : _getParentBreadcrumbs4.breadcrumbs) !== null && _getParentBreadcrumbs3 !== void 0 ? _getParentBreadcrumbs3 : [],
        payload
      });
    }, [getParentBreadcrumbs, eventDispatch]);
    return breadcrumbedEventDispatch;
  };
  return {
    Listener,
    useEventDispatch,
    useBreadcrumbedEventDispatch
  };
};

/**
 * Wraps your component with a {@link BreadcrumbComponent}
 */
export const withBreadcrumb = (Component, Breadcrumb, name,
// TODO: Fix up this type
breadcrumbFields = emptyObject) => {
  const ComponentWithBreadcrumb = props => /*#__PURE__*/React.createElement(Breadcrumb, _extends({}, breadcrumbFields, {
    name: name
  }), /*#__PURE__*/React.createElement(Component, props));
  return ComponentWithBreadcrumb;
};